import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Flex,
  Heading,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  format as formatDate,
  parseISO as parseISODate,
  isBefore as isBeforeDate,
  addMinutes,
  startOfDay,
} from "date-fns";
import { dateFnsOptions } from "../../../_utils/dateFnsLocale";
import { trackBasicEvent } from "../../../_utils/eventsTracker";
import { getCurrentTimezoneDate } from "../../../_utils/date";
import { translate as trans } from "../Trans/Trans";
import DatePicker from "../DatePicker/DatePicker";
import TimezonePicker from "../TimezonePicker/TimezonePicker";
import TimePicker from "../TimePicker/TimePicker";

const PostEditorDateTimePicker = ({
  date,
  time,
  timezone,
  defaultTimezone,
  required,
  showTimeClearButton,
  onChange,
}) => {
  const minutesStep = 15;
  const [state, setState] = useState();
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const today = useMemo(
    () => getCurrentTimezoneDate(state?.timezone),
    [state?.timezone]
  );
  const dateTime = useMemo(
    () =>
      state?.date && state?.time
        ? parseISODate(`${state?.date} ${state?.time}`)
        : undefined,
    [state]
  );

  const handleDayChange = useCallback(
    (day) => {
      const lastDateTime =
        dateTime ||
        addMinutes(getCurrentTimezoneDate(state?.timezone), minutesStep);
      const newDate = isBeforeDate(day, today) ? today : day;

      setState({
        ...state,
        date: formatDate(newDate, "yyyy-MM-dd", dateFnsOptions()),
        time: formatDate(lastDateTime, "HH:mm", dateFnsOptions()),
      });

      trackBasicEvent("post-editor", "click", "datetime picker - date change");
    },
    [setState, state, dateTime, today]
  );

  const handleTimeChange = useCallback(
    (newDateTime) => {
      const newDate = isBeforeDate(newDateTime, today) ? today : newDateTime;

      setState({
        ...state,
        date:
          state?.date || formatDate(newDate, "yyyy-MM-dd", dateFnsOptions()),
        time: formatDate(newDateTime, "HH:mm", dateFnsOptions()),
      });

      trackBasicEvent("post-editor", "click", "datetime picker - time change");
    },
    [setState, state, today]
  );

  const handleTimezoneChange = useCallback(
    (option) => {
      const tz = option.value;
      const newToday = startOfDay(getCurrentTimezoneDate(tz));
      const newDate = isBeforeDate(dateTime, newToday) ? newToday : dateTime;

      setState({
        ...state,
        date: dateTime
          ? formatDate(newDate, "yyyy-MM-dd", dateFnsOptions())
          : undefined,
        timezone: tz,
      });

      trackBasicEvent(
        "post-editor",
        "click",
        "datetime picker - timezone change"
      );
    },
    [setState, state, dateTime]
  );

  const handleClearClick = useCallback(() => {
    setState({
      ...state,
      date: undefined,
      time: undefined,
      timezone: defaultTimezone,
    });

    trackBasicEvent("post-editor", "click", "datetime picker - clear");
  }, [setState, state, defaultTimezone]);

  useEffect(() => {
    if (state === undefined) {
      return;
    }

    if (required && !dateTime) {
      const tz = timezone || defaultTimezone;
      const newDateTime = addMinutes(getCurrentTimezoneDate(tz), minutesStep);

      setState({
        date: formatDate(newDateTime, "yyyy-MM-dd", dateFnsOptions()),
        time: formatDate(newDateTime, "HH:mm", dateFnsOptions()),
        timezone: tz,
      });
    } else if (defaultTimezone && !state.timezone) {
      setState({
        ...state,
        timezone: defaultTimezone,
      });
    } else if (
      state.time !== time ||
      state.date !== date ||
      state.timezone !== timezone
    ) {
      onChange?.({
        ...state,
      });
    }
  }, [state]);

  useEffect(() => {
    setState({
      ...state,
      timezone,
    });
  }, [timezone]);

  useEffect(() => {
    setState({
      ...state,
      date,
    });
  }, [date]);

  useEffect(() => {
    setState({
      ...state,
      time,
    });
  }, [time]);

  useEffect(() => {
    setState({
      date,
      time,
      timezone,
    });
  }, []);

  return (
    <Flex direction={isSmallScreen ? "column" : "row"}>
      <Box sx={{ fontSize: "xs" }}>
        <DatePicker
          disabledDays={{ before: today }}
          month={dateTime}
          selectedDays={dateTime}
          onDayClick={handleDayChange}
        />
      </Box>
      <Flex
        width="12rem"
        flexDirection="column"
        alignItems="stretch"
        justifyContent="center"
        paddingTop={isSmallScreen ? "2" : null}
        paddingLeft={isSmallScreen ? null : "2"}
      >
        <Box textAlign="center">
          <Heading fontSize="sm" marginBottom="2">
            {trans("post_editor.date_time.time")}
          </Heading>
          <TimePicker size="sm" date={dateTime} onChange={handleTimeChange} />
        </Box>
        <Box marginTop="4" textAlign="center">
          <Heading fontSize="sm" marginBottom="2">
            {trans("post_editor.date_time.timezone")}
          </Heading>
          <Box>
            <TimezonePicker
              size="sm"
              onChange={handleTimezoneChange}
              value={state?.timezone}
            />
          </Box>
        </Box>
        {showTimeClearButton ? (
          <Box marginTop="4" textAlign="center">
            <Button
              size="sm"
              variant="outline"
              onClick={handleClearClick}
              isDisabled={
                !dateTime &&
                (!state?.timezone || state?.timezone === defaultTimezone)
              }
            >
              {required
                ? trans("post_editor.date_time.reset_time")
                : trans("post_editor.date_time.clear_time")}
            </Button>
          </Box>
        ) : null}
      </Flex>
    </Flex>
  );
};

PostEditorDateTimePicker.propTypes = {
  date: PropTypes.string,
  time: PropTypes.string,
  timezone: PropTypes.string,
  defaultTimezone: PropTypes.string,
  required: PropTypes.bool,
  showTimeClearButton: PropTypes.bool,
  onChange: PropTypes.func,
};

PostEditorDateTimePicker.defaultProps = {
  date: undefined,
  time: undefined,
  timezone: undefined,
  defaultTimezone: undefined,
  required: undefined,
  showTimeClearButton: undefined,
  onChange: undefined,
};

export default memo(PostEditorDateTimePicker);
